import classNames from "classnames";
import { createPortal } from "react-dom";
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import styles from "./Tooltip.module.css";

export interface TooltipProps {
  children: React.ReactNode;
  content: string | React.ReactNode;
  position?: "right" | "left" | "top" | "bottom";
  delay?: number;
  className?: string;
  variant?: "default" | "multiline";
  /**
   * Element used for the wrapper. Defaults to `div`.
   *
   * Needed because a `div` is not valid inside every parent — wrapping an
   * `<li>` in a `<div>` inside a `<ul>` breaks list semantics for screen
   * readers, which is exactly what NavigationItem was doing.
   */
  wrapperTag?: "div" | "span" | "li";
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = "right",
  delay = 150,
  className,
  variant = "default",
  wrapperTag: Wrapper = "div",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  /**
   * Positioning happens after the first paint, so without this the tooltip is
   * briefly at 0,0. Kept invisible until it has measured coordinates.
   */
  const [isPositioned, setIsPositioned] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  const updateTooltipPosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 12;

    let left = 0;
    let top = 0;

    if (position === "bottom") {
      top = triggerRect.bottom + margin;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    } else if (position === "top") {
      top = triggerRect.top - tooltipRect.height - margin;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    } else if (position === "left") {
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.left - tooltipRect.width - margin;
    } else {
      // right (default)
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.right + margin;
    }

    // Clamp to viewport with margin
    if (left + tooltipRect.width > viewportWidth - margin) {
      left = viewportWidth - tooltipRect.width - margin;
    }
    if (left < margin) left = margin;
    if (top + tooltipRect.height > viewportHeight - margin) {
      top = viewportHeight - tooltipRect.height - margin;
    }
    if (top < margin) top = margin;

    setTooltipStyle({ left, top });
    setIsPositioned(true);
  }, [position, variant]);

  const clearPending = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const showTooltip = () => {
    clearPending();
    timeoutRef.current = window.setTimeout(() => setIsVisible(true), delay);
  };

  /** Focus should not wait — a keyboard user has already committed. */
  const showImmediately = () => {
    clearPending();
    setIsVisible(true);
  };

  const hideTooltip = () => {
    clearPending();
    setIsVisible(false);
    // Re-measure on the next show: the trigger may have moved (sticky bars,
    // scrolled lists), so a stale position would flash in the wrong place.
    setIsPositioned(false);
  };

  useEffect(() => clearPending, []);

  // Escape dismisses without moving focus — WAI-ARIA tooltip behaviour.
  useEffect(() => {
    if (!isVisible) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsVisible(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isVisible]);

  // Recalculate position on resize/scroll while visible
  useEffect(() => {
    if (!isVisible) return undefined;
    const handler = () => updateTooltipPosition();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [isVisible, updateTooltipPosition, variant]);

  // Ensure we measure after render for accurate centering
  useLayoutEffect(() => {
    if (isVisible) updateTooltipPosition();
  }, [content, isVisible, updateTooltipPosition]);

  // An empty tooltip is a no-op wrapper, not an empty bubble. Callers pass ''
  // to mean "no tooltip right now" (e.g. an action that is currently enabled).
  if (content === "" || content === null || content === undefined) {
    return <>{children}</>;
  }

  const tooltipClasses = classNames(
    styles.tooltip,
    variant === "default" && styles[`tooltip--${position}`],
    styles.portaled,
    {
      [styles.visible]: isVisible && isPositioned,
      [styles.multiline]: variant === "multiline",
    },
    className,
  );

  /**
   * Rendered into <body>, not next to the trigger.
   *
   * An absolutely-positioned tooltip lives in its parent's stacking context, so
   * anything painted later — a sticky action bar, a card, the sidebar itself —
   * covered it. Raising z-index inside that context cannot help. A portal at
   * --z-tooltip escapes every ancestor's stacking and overflow.
   */
  const tooltipNode = (
    <div
      className={tooltipClasses}
      id={tooltipId}
      ref={tooltipRef}
      role="tooltip"
      style={tooltipStyle}
    >
      {content}
      {variant === "default" && <div className={styles.arrow} />}
    </div>
  );

  return (
    <Wrapper
      // aria-describedby, not aria-labelledby: a tooltip supplements the
      // accessible name, it does not replace it. Kept on the wrapper (rather
      // than cloned onto the child) so this works for any children shape.
      aria-describedby={tooltipId}
      className={styles.tooltipContainer}
      onBlur={hideTooltip}
      onFocus={showImmediately}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      ref={triggerRef as React.Ref<never>}
    >
      {children}
      {typeof document !== "undefined"
        ? createPortal(tooltipNode, document.body)
        : null}
    </Wrapper>
  );
};

export { Tooltip };
